import { Box, Divider, Stack, SxProps, Theme, Typography } from '@mui/material';
import React, { ReactNode, useMemo } from 'react';

import PageHeader from '../PageHeader';
import { LightTooltip } from '../WhiteTooltip';

type Currency = 'CLP' | 'USD' | 'UF';

const CURRENCY_SYMBOL: Record<Currency, string> = {
  CLP: 'CLP',
  USD: 'USD',
  UF: 'UF',
};

const formatValue = (value: number, currency: Currency): string => {
  if (currency === 'UF') {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDisplay = (value: number, currency: Currency): string => {
  const formatted = formatValue(value, currency);
  return `$${formatted}`;
};

export interface CalculatorRowTooltipItem {
  label: string;
  value: string;
}

export interface CalculatorRow {
  label: string;
  value: number;
  tooltip?: CalculatorRowTooltipItem[];
  hidden?: boolean;
  indent?: boolean;
}

export interface CalculatorCardProps {
  title?: string;
  icon?: ReactNode;
  chipLabel?: string;
  bigResult?: number;
  currency?: Currency;
  rows: CalculatorRow[];
  highlightLastRows?: number;
  sx?: SxProps<Theme>;
  showHeader?: boolean;
}

const CalculatorRowTooltip: React.FC<{
  items: CalculatorRowTooltipItem[];
  children: React.ReactElement;
}> = ({ items, children }) => (
  <LightTooltip
    title={
      <Stack spacing={0.5} sx={{ p: 0.5 }}>
        {items.map((item, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              minWidth: 140,
            }}
          >
            <Typography variant="caption">{item.label}</Typography>
            <Typography variant="caption" fontWeight={600}>
              {item.value}
            </Typography>
          </Box>
        ))}
      </Stack>
    }
  >
    {children}
  </LightTooltip>
);

const NormalRow: React.FC<{
  row: CalculatorRow;
  currency: Currency;
}> = ({ row, currency }) => {
  const content = (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ pl: row.indent ? 3 : 0 }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={row.indent ? { fontSize: '0.65rem' } : undefined}
        >
          {row.label}
        </Typography>
      </Stack>
      <Typography
        variant="caption"
        fontWeight={600}
        sx={
          row.indent
            ? { fontSize: '0.65rem', color: 'text.secondary' }
            : undefined
        }
      >
        {formatDisplay(row.value, currency)} {CURRENCY_SYMBOL[currency]}
      </Typography>
    </Stack>
  );

  if (row.tooltip?.length) {
    return (
      <CalculatorRowTooltip items={row.tooltip}>{content}</CalculatorRowTooltip>
    );
  }

  return content;
};

const HighlightedRow: React.FC<{
  row: CalculatorRow;
  currency: Currency;
}> = ({ row, currency }) => {
  const content = (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="body2" fontWeight={600}>
          {row.label}
        </Typography>
      </Stack>
      <Typography variant="body2" fontWeight={700} color="primary">
        {formatDisplay(row.value, currency)} {CURRENCY_SYMBOL[currency]}
      </Typography>
    </Stack>
  );

  if (row.tooltip?.length) {
    return (
      <CalculatorRowTooltip items={row.tooltip}>{content}</CalculatorRowTooltip>
    );
  }

  return content;
};

const CalculatorCard: React.FC<CalculatorCardProps> = ({
  title = 'Detalles de Simulación',
  icon,
  chipLabel,
  bigResult,
  currency = 'CLP',
  rows,
  highlightLastRows = 1,
  showHeader = true,
  sx,
}) => {
  const visibleRows = useMemo(() => rows.filter((r) => !r.hidden), [rows]);

  const n = Math.max(0, Math.min(highlightLastRows, visibleRows.length));
  const normalRows = n > 0 ? visibleRows.slice(0, -n) : visibleRows;
  const highlightedRows = n > 0 ? visibleRows.slice(-n) : [];

  return (
    <Box sx={sx}>
      {showHeader && (
        <PageHeader variant="subHeader">
          <PageHeader.Main>
            <PageHeader.TitleSection
              title={title}
              iconType="calculate"
              icon={icon}
              subtitle={chipLabel}
            />
            {bigResult != null && (
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="flex-end"
                  spacing={0.75}
                >
                  <Typography variant="h4" color="primary" fontWeight={700}>
                    {formatDisplay(bigResult, currency)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {CURRENCY_SYMBOL[currency]}
                  </Typography>
                </Stack>
              </Box>
            )}
          </PageHeader.Main>
        </PageHeader>
      )}

      <Box
        sx={{
          backgroundColor: 'grey.50',
          p: 1.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Stack spacing={1}>
          {normalRows.map((row, index) => (
            <NormalRow
              key={`${row.label}-${index}`}
              row={row}
              currency={currency}
            />
          ))}
          {highlightedRows.length > 0 && (
            <>
              <Divider sx={{ my: 0.5 }} />
              {highlightedRows.map((row, index) => (
                <HighlightedRow
                  key={`${row.label}-hl-${index}`}
                  row={row}
                  currency={currency}
                />
              ))}
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default CalculatorCard;
